import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
    Box,
    Typography,
    Card,
    Chip,
    IconButton,
    CardContent,
    Grid,
    Tooltip as TooltipCustom,
    useMediaQuery,
    Backdrop,
    CircularProgress,
    useTheme,
    Stack,
} from "@mui/material";
import { Menu as MenuIcon, Help as HelpIcon } from "@mui/icons-material";
import { useMaterialUIController, setMiniSidenav } from "context";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import { UserMessage } from "components/ChatBot/UserMessage";
import { AssistantMessage } from "components/ChatBot/AssistantMessage";
import { streamChat } from "api/chatStream";

import "./intelligence.css";

const Intelligencepage = ({ subcategoryToken: propSubcategoryToken }) => {
    const { state } = useLocation();
    // console.log('fef', state.kpi.value)

    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav } = controller;
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px
    const [text, setText] = useState("");
    const textareaRef = useRef(null);

    useEffect(() => {
        // Use predefined message if available, otherwise use kpi value, otherwise empty string
        setText(state?.message ?? state?.kpi?.value ?? "");
    }, [state]);
    useEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        textarea.style.height = "auto";
        textarea.style.height = Math.min(textarea.scrollHeight, 180) + "px";
    }, [text]);


    // Chat state (inlined, reuse ChatContainer logic)
    const [messages, setMessages] = useState([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [currentRequestId, setCurrentRequestId] = useState(null);
    const abortControllerRef = useRef(null);

    // State for API data (page-level loading)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const header = document.querySelector('.MuiAppBar-root');
        if (header) {
            header.style.display = 'none';
        }
    }, []);

    const isXs = useMediaQuery(theme.breakpoints.down("sm"));

    useEffect(() => {
        if (isXs) {
            const scrollY = window.scrollY;

            document.body.style.position = "fixed";
            document.body.style.top = `-${scrollY}px`;
            document.body.style.left = "0";
            document.body.style.right = "0";
            document.body.style.width = "100%";
        } else {
            const top = document.body.style.top;
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.left = "";
            document.body.style.right = "";
            document.body.style.width = "";

            // restore scroll position
            if (top) {
                window.scrollTo(0, -parseInt(top || "0"));
            }
        }
    }, [isXs]);

    // Toggle sidenav from the page menu icon
    const isTogglingSidenavRef = useRef(false);
    const handleToggleSidenav = useCallback(() => {
        if (isTogglingSidenavRef.current) return;
        isTogglingSidenavRef.current = true;
        // Defer to next frame to avoid blocking heavy charts paint
        requestAnimationFrame(() => {
            setMiniSidenav(dispatch, !miniSidenav);
            setTimeout(() => {
                isTogglingSidenavRef.current = false;
            }, 150);
        });
    }, [dispatch, miniSidenav]);

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
            } catch (error) {
                console.error('Failed loading static mocks or transforming data:', error);
                setError('Failed to load static mock data. Check competitive_analysis/data/allMocks.json');
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, [location.search, propSubcategoryToken]);


    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "";
        };
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setCurrentConversationId(null);
            setCurrentRequestId(null);
            if (abortControllerRef.current) {
                abortControllerRef.current();
            }
        };
    }, []);

    const handleSend = async (userMessage) => {
        if (!userMessage.trim() || isStreaming) return;

        // user message
        const userMsg = { role: "user", blocks: [{ type: "text", content: userMessage }] };
        setMessages((prev) => [...prev, userMsg]);

        // placeholder assistant message
        setMessages((prev) => [...prev, { role: "assistant", blocks: [] }]);
        setIsStreaming(true);

        const handleChunk = (payload, conversationId, requestId) => {
            const blockType = payload?.type || payload?.rich?.type;
            const nonContentTypes = [
                "status_bar_update",
                "task_tracker_update",
                "chat_input_update",
                "doorsharp-status-bar",
                "doorsharp-task-tracker",
                "doorsharp-chat-input",
                "dataframe",
            ];

            if (nonContentTypes.includes(blockType) || payload?.id?.startsWith("doorsharp-")) {
                return;
            }

            if (conversationId && !currentConversationId) setCurrentConversationId(conversationId);
            if (requestId && !currentRequestId) setCurrentRequestId(requestId);

            setMessages((prev) => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                const lastMessage = newMessages[lastIndex];

                if (lastMessage && lastMessage.role === "assistant") {
                    newMessages[lastIndex] = {
                        ...lastMessage,
                        blocks: [...lastMessage.blocks, payload],
                    };
                }

                return newMessages;
            });
        };

        const handleError = (error) => {
            console.error("Streaming error:", error);
            setIsStreaming(false);

            setMessages((prev) => {
                const newMessages = [...prev];
                const lastIndex = newMessages.length - 1;
                const lastMessage = newMessages[lastIndex];

                if (lastMessage && lastMessage.role === "assistant") {
                    if (lastMessage.blocks.length === 0) {
                        newMessages[lastIndex] = {
                            ...lastMessage,
                            blocks: [
                                {
                                    type: "text",
                                    content: "Sorry, an error occurred while processing your request.",
                                },
                            ],
                        };
                    }
                }

                return newMessages;
            });
        };

        const handleComplete = () => {
            setIsStreaming(false);
        };

        try {
            const abortFn = await streamChat(
                userMessage,
                currentConversationId || null,
                currentRequestId || null,
                handleChunk,
                handleError,
                handleComplete
            );
            abortControllerRef.current = abortFn;
        } catch (error) {
            handleError(error);
        }
    };

    const quickActions = [
        { title: "Units Sold by Sub Brand", subtitle: "Create a stacked bar chart" },
        { title: "Net Sales by Category", subtitle: "Create a line chart" },
        { title: "Lowest Performing SKU's", subtitle: "Create a list for this year" },
        { title: "Promotion Spend vs Return", subtitle: "Create a stacked bar chart" },
        { title: "Gross Sales by Quarter", subtitle: "Create a pie chart" },
    ];

    const handleQuickAction = (action) => {
        if (isStreaming) return;
        const prompt = `${action.title} - ${action.subtitle}`;
        handleSend(prompt);
        setText("");
    };
    const TypingIndicator = () => (
        <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <Box className="dot" />
            <Box className="dot" />
            <Box className="dot" />
        </Box>
    );

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault(); // stop new line
            if (!text.trim() || isStreaming) return;
            handleSend(text);
            setText("");
        }
    };

    return (
        <Box sx={{
            ml: 0,
            transition: "margin-left 0.25s ease",
            "& .MuiBox-root": { backgroundImage: "none" }
        }} style={{
            minHeight: { xs: 'calc(100vh - 120px)', md: '100vh', lg: '100vh' },
            padding: isMobile ? '16px 20px 0px 20px' : '0px 40px 0px 40px',
            // paddingBottom: '100px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            // background: "red"
        }}>
            {/* Loading Backdrop */}
            <Backdrop
                open={loading}
                sx={{
                    position: 'fixed',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <CircularProgress color="primary" />
            </Backdrop>

            {/* MAIN WRAPPER */}
            <Box
                sx={{
                    display: "flex",
                    height: { xs: "calc(100vh - 143px)", sm: "calc(100vh - 143px)", md: "calc(100vh - 71px)" },
                    flexDirection: "column",
                    overflow: {
                        xs: "hidden !important",
                        sm: "hidden !important",
                        md: "hidden !important"
                    },
                    // background: "green"
                }}
            >
                {/* LEFT SIDE (20% WIDTH) */}
                {/* <Box
                    sx={{
                        width: "20%",
                        minWidth: "220px",
                        maxWidth: "300px",
                        borderRight: "1px solid rgba(0,0,0,0.05)",
                        display: "flex",
                        flexDirection: "column",
                        pt: 3,
                        pl: 1,
                        pr: 2,
                        overflowY: "auto",
                        // background:"blue !important"
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#9bc1e8",
                            mb: 1,
                        }}
                    >
                        Agent History
                    </Typography>

                    {[
                        "Net Sales by Quarter...",
                        "Out of Stock SKU’s",
                        "Units Sold by TX County...",
                        "Monster vs C4 Gross...",
                        "Least Profitable SKU’s",
                        "Current Active Nutrition...",
                        "Distributor Pie Chart",
                        "Fastest Growing Counties...",
                        "Average Discount by Chain",
                        "Target vs Walmart Units...",
                    ].map((item, i) => (
                        <Typography
                            key={i}
                            sx={{
                                opacity: 0.7,
                                fontSize: "13px",
                                mb: "4px",
                                color: "#c2ccda",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                cursor: "pointer",
                                "&:hover": { color: "#8fa2c1" },
                            }}
                        >
                            {item}
                        </Typography>
                    ))}
                </Box> */}

                {/* RIGHT SIDE (REMAINING WIDTH) */}
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "100%",

                        // background:"yellow"
                        // overflow: "hidden"

                        // py: 2,
                        // px: 3,
                    }}
                >
                    {/* CENTER WHITE CANVAS */}
                    <Box
                        sx={{
                            flex: 1,
                            borderRadius: "8px",
                            height: "100%",
                            boxShadow: "0px 1px 6px rgba(0,0,0,0.05)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-end",
                            pb: { xs: 0, md: 0 },
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        {/* MESSAGES AREA */}
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: "auto",
                                px: { xs: "0px", md: 3 },
                                pt: { xs: "14px", md: 3 },
                            }}
                        >
                            {messages.map((msg, index) => {
                                const isUser = msg.role === "user";
                                const isLastAssistant =
                                    !isUser && isStreaming && index === messages.length - 1
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: "flex",
                                            justifyContent: isUser ? "flex-end" : "flex-start",
                                            mb: 1.5,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                maxWidth: isUser ? "70%" : "75%",
                                                background: isUser ? "#1672B980" : "#1672B9",
                                                color: isUser ? "#fff" : "#FFFFFF",
                                                borderRadius: {
                                                    xs: '8px !important',
                                                    sm: '8px !important',
                                                    md: "24px !important"
                                                },
                                                px: { xs: "16px", sm: "16px", md: 2 },
                                                py: { xs: "10px", sm: "10px", md: "10px !important" },
                                                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                                border: isUser ? "none" : "1px solid #e6e8f0",
                                                fontSize: { xs: "16px", sm: "16px", md: "13px" },
                                                fontWeight: 300
                                            }}>
                                            {isUser ? (
                                                <UserMessage message={msg.blocks[0]?.content} />
                                            ) : isLastAssistant && (!msg.blocks || msg.blocks.length === 0) ? (
                                                // Loading state
                                                <TypingIndicator />
                                                // OR <CircularProgress size={16} sx={{ color: "#fff" }} />
                                            ) : (
                                                <AssistantMessage
                                                    blocks={msg.blocks}
                                                    isStreaming={isStreaming && index === messages.length - 1}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>

                        {/* QUICK ACTION CARDS - Show only before conversation starts */}
                        {messages.length === 0 && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: {
                                        xs: "flex-start",
                                        sm: "flex-start",
                                        md: "flex-start",
                                        lg: "center"
                                    },
                                    gap: { xs: "16px", sm: "16px", md: "16px" },
                                    // flexWrap: "wrap",
                                    mb: { xs: "6px", md: "10px" },
                                    mx: { xs: "8px", sm: "12px", md: "16px" },
                                    width: "100%",
                                    /* ADD THESE */
                                    maxHeight: { xs: "180px", sm: "220px", md: "260px" },
                                    overflowY: "hidden",
                                    overflowX: "auto",
                                    pr: { xs: "24px", sm: "24px", md: "24px" },
                                    scrollPaddingRight: "24px",
                                    WebkitOverflowScrolling: "touch",

                                }}
                            >
                                {quickActions.map((item, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            background: "#9AD3FF1A",
                                            border: "1px solid #1672B91A",
                                            borderRadius: "8px",
                                            // width: "fit-content",
                                            // maxWidth: "none",
                                            minWidth: { xs: "auto", sm: "auto", md: "160px" },
                                            flex: { xs: "1 1 calc(50% - 6px)", sm: "0 0 auto" },
                                            flexShrink: 0,
                                            width: "max-content",
                                            p: { xs: "12px", sm: "12px", md: "12px" },
                                            cursor: "pointer",
                                            textAlign: "center",
                                            boxShadow: "0px 2px 6px rgba(0,0,0,0.06)",
                                            "&:hover": { background: "#e7f1ff" },
                                            fontSize: "13px",
                                            whiteSpace: "nowrap",
                                            height: { xs: "56px", sm: "56px", md: "56px" },
                                            textAlign: "left"
                                        }}
                                        onClick={() => handleQuickAction(item)}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: { xs: "12px", sm: "12px", md: "12px" },
                                                fontWeight: 500,
                                                color: "#4085BA",
                                                mb: 0.2,
                                                lineHeight: 1,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {item.title}
                                        </Typography>
                                        <Typography sx={{ fontSize: { xs: "12px", md: "12px" }, color: "#11111180", whiteSpace: "nowrap", fontWeight: 200 }}>
                                            {item.subtitle}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}


                        {/* CHAT INPUT */}
                        <Box
                            sx={{
                                width: "100%",
                                p: { xs: "12px 4px", sm: "12px 4px", md: "12px 12px", lg: "12px 136px" },
                            }}
                        >
                            <Box
                                sx={{
                                    background: "#fff",
                                    width: "100%",
                                    minHeight: { xs: "46px", md: "50px" },
                                    borderRadius: "30px",
                                    boxShadow: {
                                        xs: "0px 2px 24px rgba(22,114,185,0.12)",
                                        md: "0px 2px 24px rgba(22,114,185,0.15)",
                                    },
                                    display: "flex",
                                    border: "1px solid rgba(17, 17, 17, 0.1)",
                                    alignItems: "center",
                                    px: { xs: 1.5, sm: 2, },
                                    pl: { md: "24px" },
                                    pr: { md: "8px" },
                                }}
                            >
                                {/* TEXTAREA WRAPPER */}
                                <Box sx={{ flex: 1, display: "flex" }}>
                                    <textarea
                                        ref={textareaRef}
                                        className="kpi-chat-textarea"
                                        placeholder="Let's talk Nutrabolt!"
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        style={{
                                            flex: 1,
                                            border: "none",
                                            outline: "none",
                                            fontSize: isMobile ? "15px" : "15px",
                                            color: "#111111",
                                            fontFamily: "Inter, sans-serif",
                                            background: "transparent",
                                            resize: "none",
                                            overflowY: "auto",
                                            overflowX: "hidden",
                                            paddingTop: isMobile ? "14px" : "16px",
                                            paddingBottom: isMobile ? "11px" : "13px",
                                            lineHeight: "1.4",
                                            maxHeight: isMobile ? "120px" : "180px",
                                            fontWeight: 400
                                        }}
                                        rows={1}
                                    />
                                </Box>

                                {/* SEND BUTTON */}
                                <Box
                                    sx={{
                                        width: { xs: 36, md: 36 },
                                        height: { xs: 36, md: 36 },
                                        background: "#1672B9",
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        cursor: "pointer",
                                        // boxShadow: "0px 5px 20px rgba(13,110,253,0.45)",
                                        ml: 1,
                                        flexShrink: 0,
                                        // mt: { xs: "7.2px", md: "7.2px" },
                                    }}
                                    onClick={() => {
                                        if (!text.trim() || isStreaming) return;
                                        handleSend(text);
                                        setText("");
                                    }}
                                >
                                    <ArrowForwardIcon
                                        sx={{ color: "#fff", fontSize: { xs: "18px", md: "20px" }, transform: "translateX(1px)" }}
                                    />
                                </Box>
                            </Box>
                        </Box>



                    </Box>
                </Box>
            </Box>


        </Box>
    );
};

export default Intelligencepage;